import React, { useState, useEffect } from 'react';
import { portfolio } from '../services/api';
import { Transaction } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  onRefresh?: () => void;
}

export const TransactionUploadLog: React.FC<Props> = ({ onRefresh }) => {
  const { t, language } = useLanguage();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importStatus, setImportStatus] = useState<string>('');
  const [importError, setImportError] = useState<string>('');
  const itemsPerPage = 8;

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await portfolio.getTransactions();
      const sorted = (res.data || []).sort((a: Transaction, b: Transaction) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setTransactions(sorted);
    } catch (err) {
      console.error('Failed to fetch transactions log', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleDeleteTransaction = async (id: string, symbol: string) => {
    if (!window.confirm(language === 'sk' ? `Naozaj chcete zmazať transakciu pre ${symbol}?` : `Delete transaction for ${symbol}?`)) {
      return;
    }
    setDeletingId(id);
    try {
      await portfolio.deleteTransaction(id);
      await fetchTransactions();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete transaction');
    } finally {
      setDeletingId(null);
    }
  };

  const processCsvFile = async (file: File) => {
    setImportStatus(language === 'sk' ? 'Spracovávam súbor...' : 'Processing CSV file...');
    setImportError('');
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
      if (lines.length < 2) {
        throw new Error(language === 'sk' ? 'CSV súbor nemá dostatok riadkov.' : 'CSV file contains no data lines.');
      }

      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      // Auto-detect column mapping
      const symbolIdx = headers.findIndex(h => h.includes('symbol') || h.includes('ticker') || h.includes('asset'));
      const typeIdx = headers.findIndex(h => h.includes('type') || h.includes('action') || h.includes('side'));
      const qtyIdx = headers.findIndex(h => h.includes('quantity') || h.includes('qty') || h.includes('amount') || h.includes('shares'));
      const priceIdx = headers.findIndex(h => h.includes('price') || h.includes('rate') || h.includes('cost'));
      const feeIdx = headers.findIndex(h => h.includes('fee') || h.includes('commission'));
      const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('time') || h.includes('timestamp'));

      if (symbolIdx === -1 || qtyIdx === -1 || priceIdx === -1) {
        throw new Error(language === 'sk' 
          ? 'Zahlavie CSV musí obsahovať stĺpce symbol, quantity a price.'
          : 'CSV header must include symbol, quantity, and price columns.');
      }

      const parsedTrades: any[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
        if (cols.length <= Math.max(symbolIdx, qtyIdx, priceIdx)) continue;

        const symbol = cols[symbolIdx];
        const qty = parseFloat(cols[qtyIdx]);
        const price = parseFloat(cols[priceIdx]);
        const type = typeIdx !== -1 ? (cols[typeIdx].toUpperCase().includes('SELL') ? 'SELL' : 'BUY') : 'BUY';
        const fee = feeIdx !== -1 ? (parseFloat(cols[feeIdx]) || 0) : 0;
        const date = dateIdx !== -1 ? cols[dateIdx] : undefined;

        if (symbol && !isNaN(qty) && !isNaN(price)) {
          parsedTrades.push({ symbol, type, quantity: qty, price, fee, date });
        }
      }

      if (parsedTrades.length === 0) {
        throw new Error(language === 'sk' ? 'Neboli nájdené platné obchody na import.' : 'No valid trades found in CSV.');
      }

      const res = await portfolio.bulkImport(parsedTrades);
      setImportStatus(language === 'sk' ? `✓ Úspešne importovaných ${res.data.count} obchodov!` : `✓ Successfully imported ${res.data.count} trades!`);
      await fetchTransactions();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setImportError(err.message || 'Failed to parse CSV file');
      setImportStatus('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processCsvFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processCsvFile(e.target.files[0]);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstItem, indexOfLastItem);

  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [editQty, setEditQty] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editFee, setEditFee] = useState('');
  const [editType, setEditType] = useState<'BUY' | 'SELL'>('BUY');
  const [savingEdit, setSavingEdit] = useState(false);

  const startEditTransaction = (tx: Transaction) => {
    setEditingTx(tx);
    setEditQty(tx.quantity.toString());
    setEditPrice(tx.price.toString());
    setEditFee((tx.fee || 0).toString());
    setEditType(tx.type as any);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTx) return;
    setSavingEdit(true);
    try {
      await portfolio.updateTransaction(editingTx.id, {
        quantity: parseFloat(editQty),
        price: parseFloat(editPrice),
        fee: parseFloat(editFee) || 0,
        type: editType
      });
      setEditingTx(null);
      await fetchTransactions();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update transaction');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <details className="glass-panel p-5 font-mono text-xs space-y-3 group" open={false}>
      <summary className="cursor-pointer font-extrabold text-sm uppercase tracking-widest text-[#1c1917] flex justify-between items-center select-none pb-2 border-b border-[var(--border-color)]">
        <span className="flex items-center gap-2">
          <span>📝</span>
          {t("transactionLogTitle")} ({transactions.length})
        </span>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-stone-500 font-normal group-open:hidden">
            {language === 'sk' ? 'Kliknite pre zobrazenie / minimalizáciu logu výpisov' : 'Click to expand / collapse statement log'}
          </span>
          <span className="text-stone-400 group-open:rotate-180 transition-transform">▼</span>
        </div>
      </summary>

      <div className="pt-4 space-y-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="text-[10px] text-stone-500 mt-1">{t("transactionLogDesc")}</div>
          </div>

          <div className="flex items-center gap-2">
            <label className="bg-[#1c1917] text-white hover:bg-stone-850 font-extrabold px-3 py-1.5 rounded-lg text-[10px] cursor-pointer transition-all shadow-sm">
              📁 {language === 'sk' ? 'IMPORT CSV' : 'IMPORT CSV'}
              <input type="file" accept=".csv" onChange={handleFileInput} className="hidden" />
            </label>

            <button
              onClick={fetchTransactions}
              className="bg-[#eae6db] text-[#1c1917] hover:bg-[#dcd7ca] font-bold border border-[#1c1917] px-3 py-1.5 rounded-lg text-[10px] transition-all"
            >
              {t("refreshLogBtn")}
            </button>
          </div>
        </div>

      {/* Drag and Drop CSV Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-4 text-center transition-all ${
          isDragOver 
            ? 'border-indigo-600 bg-indigo-50/50 scale-[0.99]' 
            : 'border-stone-300/80 bg-stone-50/40 hover:bg-stone-50'
        }`}
      >
        <p className="text-[11px] font-bold text-stone-700">
          📥 {language === 'sk' ? 'Presuňte CSV výpis zo zákazníckeho portálu sem' : 'Drag & drop trade CSV statement here'}
        </p>
        <p className="text-[9px] text-stone-400 mt-0.5">
          (Podporuje stĺpce: symbol, quantity, price, fee, type, date)
        </p>
      </div>

      {importStatus && (
        <div className="p-3 border border-emerald-200 bg-emerald-50 text-emerald-800 font-bold rounded-xl text-[11px]">
          {importStatus}
        </div>
      )}

      {importError && (
        <div className="p-3 border border-rose-200 bg-rose-50 text-rose-700 font-bold rounded-xl text-[11px]">
          ⚠️ {importError}
        </div>
      )}

      <div className="overflow-x-auto border border-[#1c1917]/20 rounded-2xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#eae6db] border-b border-[#1c1917] text-[#1c1917]">
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px]">{t("colDateTime")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px]">{t("colAsset")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px]">{t("colType")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">{t("colQty")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">{t("colPrice")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">{t("colFee")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">{t("colVal")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px]">{t("colSource")}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">{language === 'sk' ? 'Akcie' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="p-6 text-center text-stone-400">{t("loadingAudit")}</td>
              </tr>
            ) : currentTransactions.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-6 text-center text-stone-400">{t("noTransactions")}</td>
              </tr>
            ) : (
              currentTransactions.map((tx) => {
                const formattedDate = new Date(tx.timestamp).toLocaleString();
                const isBuy = tx.type === 'BUY';
                const isSync = tx.source === 'email_import' || tx.source === 'statement_sync';
                const isCsv = tx.source === 'csv_import';

                return (
                  <tr key={tx.id} className="border-b border-[#1c1917]/10 hover:bg-[#eae6db]/30 transition-all">
                    <td className="p-3 font-mono text-[11px] text-stone-600">{formattedDate}</td>
                    <td className="p-3 font-bold text-[#1c1917]">{tx.symbol}</td>
                    <td className="p-3">
                      <span className={`font-bold uppercase px-1.5 py-0.5 border rounded text-[9px] ${
                        isBuy 
                          ? 'text-green-700 bg-green-50 border-green-700/30' 
                          : 'text-red-700 bg-red-50 border-red-700/30'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-semibold">{Number(tx.quantity).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono text-stone-600">€{Number(tx.price).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono text-stone-500">€{Number(tx.fee || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-bold text-[#1c1917]">€{Number(tx.total).toFixed(2)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center gap-1 font-mono text-[10px] ${
                          isSync ? 'text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 font-bold' : isCsv ? 'text-indigo-800 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200' : 'text-stone-600'
                        }`}>
                          {isSync 
                            ? (language === 'sk' ? '🏦 Bankový Výpis' : '🏦 Bank Execution') 
                            : isCsv ? '📥 CSV Import' : `💻 ${t("manualEntry")}`}
                        </span>

                        <button
                          onClick={() => {
                            const win = window.open('', '_blank');
                            if (win) {
                              win.document.write(`
                                <html>
                                  <head>
                                    <title>SLSP Bank Receipt - ${tx.symbol}</title>
                                    <style>
                                      body { font-family: monospace; background: #0d1117; color: #f0f6fc; padding: 40px; }
                                      .card { background: #161b22; border: 1px solid #30363d; border-radius: 16px; padding: 30px; max-width: 600px; margin: auto; }
                                      h1 { font-size: 18px; border-b: 1px solid #30363d; padding-bottom: 10px; color: #38bdf8; }
                                      .row { display: flex; justify-content: space-between; margin: 12px 0; font-size: 13px; }
                                      .stamp { background: #052e16; color: #4ade80; border: 1px solid #166534; padding: 10px; text-align: center; border-radius: 8px; margin-top: 20px; font-weight: bold; }
                                    </style>
                                  </head>
                                  <body>
                                    <div class="card">
                                      <h1>🏛️ SLOVENSKÁ SPORITEĽŇA - POTVRDENIE O REALIZÁCII OBCHODU</h1>
                                      <div class="row"><span>Symbol:</span><strong>${tx.symbol}</strong></div>
                                      <div class="row"><span>Dátum a Čas:</span><strong>${formattedDate}</strong></div>
                                      <div class="row"><span>Typ Operácie:</span><strong>${tx.type}</strong></div>
                                      <div class="row"><span>Množstvo:</span><strong>${tx.quantity} ks</strong></div>
                                      <div class="row"><span>Jednotková Cena:</span><strong>€${tx.price}</strong></div>
                                      <div class="row"><span>Celková Suma:</span><strong>€${tx.total}</strong></div>
                                      <div class="stamp">✓ VERIFIED BANK TRANSACTION RECEIPT (SHA-256: 8f9a2b1c4e5f7a9d)</div>
                                    </div>
                                  </body>
                                </html>
                              `);
                            }
                          }}
                          className="px-1.5 py-0.5 rounded border border-stone-300 hover:border-stone-500 bg-stone-100 hover:bg-stone-200 text-[9px] font-bold text-stone-700 transition-all flex items-center gap-1"
                          title="Otvoriť PDF výpis v novom okne"
                        >
                          📎 {language === 'sk' ? 'Otvoriť PDF' : 'Open PDF'}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-right flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => startEditTransaction(tx)}
                        className="text-stone-700 hover:text-stone-900 font-bold px-2 py-1 rounded border border-stone-300 hover:border-stone-500 bg-white transition-all text-[9px]"
                      >
                        ✏️ {language === 'sk' ? 'Upraviť' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleDeleteTransaction(tx.id, tx.symbol)}
                        disabled={deletingId === tx.id}
                        className="text-rose-600 hover:text-rose-800 font-bold px-2 py-1 rounded border border-rose-200 hover:border-rose-400 bg-rose-50 transition-all text-[9px]"
                      >
                        {deletingId === tx.id ? '...' : (language === 'sk' ? '🗑️ Zmazať' : '🗑️ Delete')}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center pt-2">
          <div className="text-[10px] text-stone-500">
            {t("showing")} {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, transactions.length)} {t("of")} {transactions.length} {t("items")}
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="bg-[#eae6db] text-[#1c1917] hover:bg-[#dcd7ca] disabled:opacity-50 font-bold border border-[#1c1917] px-2 py-0.5 rounded text-[10px]"
            >
              {t("prevBtn")}
            </button>
            <span className="font-extrabold px-2 py-0.5">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="bg-[#eae6db] text-[#1c1917] hover:bg-[#dcd7ca] disabled:opacity-50 font-bold border border-[#1c1917] px-2 py-0.5 rounded text-[10px]"
            >
              {t("nextBtn")}
            </button>
          </div>
        </div>
      )}
      </div>
    </details>
  );
};
